// WHY a `tasks` row was cancelled — the closed `tasks.cancel_reason`
// vocabulary (#3339). See `../task.ts` for the file docblock.

import { z } from 'zod';

/**
 * WHY a `tasks` row was moved to `cancelled` — the discriminated reason stored
 * on `tasks.cancel_reason` by the statement that lands the cancel (#3339). A
 * closed vocabulary: {@link TASK_CANCEL_REASON_PRODUCER} is a total `Record`
 * over it, so a new reason fails typecheck until its writer is named.
 *
 * SIBLING OF TWO ADJACENT VOCABULARIES, and the borders are contract, not
 * taste. This column answers *"why is this row cancelled"*. It is NOT
 * {@link TaskHoldReason} — every member of that set is a PENDING-time reason a
 * dispatcher pass both stamps and retracts, and `normalizeTask` masks it to
 * null on every non-pending read, so a reason written there is invisible on
 * the row it describes. It is NOT {@link TaskFailureKind} — every member of
 * that set is an attempt-failure reason, read under `WHERE status = 'failed'`,
 * and a cancel reason stored there reaches no existing consumer. And it is NOT
 * the runner's `RunCancelledError.cancelReason` (`apps/runner/src/cancel.ts` —
 * `runner_reaped` / `operator_drain` / `claim_lost_on_reconnect`): that one is
 * an in-process error field that never reaches the database and says why a RUN
 * stopped on a box; this one says why a TASK ROW was cancelled by a writer.
 * The two answering different questions is why the names are deliberately not
 * shared.
 *
 * THE COLUMN IS NULLABLE AND HISTORY IS NOT BACKFILLED. Every `cancelled` row
 * written before the column existed keeps NULL, and that is the honest value —
 * nobody recorded a reason, and inventing one would corrupt the telemetry the
 * column exists to produce. Read with a `cancel_reason IS NULL` arm, never
 * with an assumption that NULL means a particular cause.
 *
 * SURVIVES REVIVAL, like `failure_kind`: `redispatchTask` can re-pend a
 * cancelled row, and no lane clears the stamp afterwards — the column records
 * the row's LAST cancel, and only `WHERE status = 'cancelled'` turns it into
 * "why is this row dead". One seam owns the clear decision by there being no
 * clear at all.
 *
 * One member per cancel path. The paths are enumerated on
 * {@link TASK_CANCEL_REASON_PRODUCER}; the statements themselves are pinned by
 * `packages/db/src/queries/task-cancel-reason-write.test.ts`, the
 * `landsCancelled()` twin of the `failure_kind` write scan.
 */
export const TaskCancelReason = {
  /**
   * A NEWER HEAD'S REVIEW DISPLACED THIS ONE. A push mints a fresh `pr_review`
   * task keyed on the new head sha, and `supersedeReviewTasks`
   * (`packages/db/src/queries/tasks/pr-lane.ts`) cancels every EARLIER
   * UNSTARTED review of the same PR — `pending`/`assigned` only; a review
   * already `in_progress` is deliberately kept (#3158) and carries no reason
   * because it is never cancelled there.
   *
   * SECOND PRODUCER (#6718): the box's own relevance check at claim. A review box that
   * reads a newer head on the PR ends the run `review_irrelevant` (uncharged) and the
   * mothership lands it here through `cancelIrrelevantReview`
   * (`packages/db/src/queries/tasks/ci-pending.ts`), fenced on the box's claim.
   */
  Superseded: 'superseded',
  /**
   * THE PULL REQUEST SETTLED — closed or merged — so every non-terminal
   * PR-lane task chasing it is dead work. `retireTasksForClosedPr`
   * (`packages/db/src/queries/tasks/pr-lane.ts`) stamps it, and the stamp is
   * BEHIND THE SAME `case when` as the status: on a MERGED PR the
   * `pr_babysit_watch` anchor lands `merged` (the same terminal the driver
   * writes when the fleet presses the button) and carries NO reason, while
   * everything else lands `cancelled` with this one. For a `pr_review` head
   * this is the operative canceller — the PR settled first (#3339 criterion 1).
   *
   * SECOND PRODUCER (#6718): a review box that reads the PR closed or merged at claim
   * ends the run `review_irrelevant`, landed here by `cancelIrrelevantReview`.
   */
  PrSettled: 'pr_settled',
  /**
   * AN OPERATOR STOPPED THE TASK — `cancelTask`
   * (`packages/db/src/queries/tasks/operator.ts`), the control-MCP stop verb.
   * A `pr_review` task whose PR is still OPEN is refused instead
   * (`notHoldingLiveMergeGate` — the cancel would release the merge gate), so
   * this reason on a review row implies the PR had already settled.
   */
  OperatorStop: 'operator_stop',
  /**
   * AN OPERATOR STOPPED THE WHOLE PLAN — `cancelPlanTasks`
   * (`packages/db/src/queries/tasks/operator.ts`) cancels every non-terminal
   * child of the plan's groups in one set-based UPDATE. Same live-merge-gate
   * carve-out as {@link OperatorStop}: `pr_review` children holding one are
   * named in `skippedReview`, never cancelled.
   */
  PlanStop: 'plan_stop',
  /**
   * A PLAN REVISION REMOVED THIS GROUP (#5128). `revisePlanGroups`
   * (`packages/db/src/queries/plan-groups/revise.ts`) deletes the unstarted
   * groups of a document the revision changed or dropped, and cancels the task
   * ONLY when the plan itself minted it
   * (`task_id = authoredTaskId(planId, group_id)`), it is still `pending` and
   * it owns no pull request — the CAS lands `WHERE status = 'pending' AND
   * pull_request_number IS NULL` and a zero-row answer throws. An adopted or
   * regroup-owned task is never cancelled here; it is named in the answer's
   * `left_running_task_ids` instead.
   *
   * NOT {@link PlanStop}: the plan lives on, this row's unit was revised away.
   * Deliberately NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted, and
   * the group row is DELETED in the same transaction — there is no group left
   * for a stop-style reader to resurrect or a plan projection to show.
   */
  PlanRevised: 'plan_revised',
  /**
   * THE BABYSIT WATCH GAVE UP — the driver decided `BabysitState.Abandoned`
   * (the watch has nothing left to act on: the PR was closed, by someone else or
   * by our own standing / `cannot_adapt` close, OR it is still OPEN with an author
   * outside `pr.auto_merge_scope` — `outOfScopeWatch`, most of them; that
   * state's docblock enumerates the producers), which
   * `babysitStateToTaskStatus` maps to `cancelled`. Written through the
   * GENERIC `transition` (`transitionTask`,
   * `packages/db/src/queries/tasks/lifecycle.ts`), which stamps
   * `status: input.to` — so no literal-`cancelled` source scan can see this
   * path, and the non-optional `cancelReason` on `TransitionInput`'s
   * `to: Cancelled` arm is the guard instead. Supplied at
   * `apps/worker/src/babysit/driver.ts#driveTask`.
   */
  Abandoned: 'abandoned',
  /**
   * THE GH-DISPATCH LANE DECLINED THE DISPATCH — `ghDispatchAdvance`
   * (`apps/worker/src/consumers/gh-dispatch.lane.ts`) concludes the lane's own
   * task row `completed → cancelled` when `handleGhDispatch` skips
   * (`repo_unresolved` / `repo_disabled` / `no_installation`): the disclosed
   * issue was never opened, so the row must not read as delivered work.
   *
   * THE SIXTH MEMBER THE ISSUE'S ENUMERATION DID NOT NAME, and its presence is
   * the mechanism working as designed rather than scope creep: #3339 traced
   * five WRITE STATEMENTS and mapped the generic `transition` to `abandoned`
   * alone, but two live FLOWS route a cancel through that one statement — the
   * babysit driver's Abandoned hop and this lane's `conclude(Cancelled)`. The
   * non-optional `cancelReason` parameter the issue mandates on
   * `TransitionInput` is exactly what makes this path unrepresentable without
   * naming it: tsc refuses the call site until the reason exists. The
   * `Record`'s stated purpose — "a sixth member fails typecheck until its
   * writer is named" — is hereby exercised.
   */
  DispatchSkipped: 'dispatch_skipped',
  /**
   * A TRIAGE RE-JUDGE PLANNED WITHOUT THIS PENDING TASK (plan 101 L6). When triage
   * judges an ask again, the tasks an earlier pass minted for it are superseded, so
   * the ask's work is described by one judgement rather than two. The ones still
   * `pending` (no box has taken them) are cancelled with this reason, together with
   * their plan groups. An `assigned`, running or finished task is never touched.
   * `cancelSupersededTriageTasks` (`packages/db/src/queries/tasks/rejudge.ts`) stamps
   * it, bumping the fence.
   *
   * NOT {@link Superseded}, and the split is what an operator reads: `superseded` is
   * the REVIEW lane's reason (a newer head's review displaced an older one), keyed
   * on a pull request's head sha. This one is the TRIAGE lane's, keyed on an ask's
   * re-judgement. Collapsing them would make "how often does a re-judge discard
   * minted work" unanswerable off `cancel_reason`.
   */
  SupersededByRejudge: 'superseded_by_rejudge',
  /**
   * THE REPO IS DISABLED WHILE A BABYSIT WATCH IS PARKED ON ITS PR. The
   * babysit reconciler (`apps/worker/src/cron/babysit-sweep.ts`) refuses a parked
   * task whose repo the acting gate names `repo_disabled`, and `repoDisabledSettlePort`
   * (`apps/worker/src/babysit/repo-disabled-settle.ts`) cancels it with this reason
   * in one status CAS, beside a durable `babysit.transition` row. Before it, the row
   * was re-refused and re-warned every tick, forever, and read as live work.
   *
   * The reason states the FACT (`repositories.enabled = false`), not who wrote it. The
   * writers that turn the column off on an existing row are these, and under each the
   * watch cannot progress:
   *   1. `disableRepositoryIfOwned` (`packages/db/src/queries/repositories/enablement.ts`), the owner's REST / MCP toggle, called from `writeEnabled` (`apps/api/src/deps/repos.ts`);
   *   2. `disableRepositoriesByGithubIds` (`packages/db/src/queries/onboarding.ts`), called from `disableRemovedRepos` (`apps/worker/src/consumers/onboarding-repo-leave.ts`) on GitHub `installation_repositories.removed`;
   *   3. the same `disableRepositoriesByGithubIds`, called from `selfHealUnknownRepo` (`apps/worker/src/fleet/presign-rpc.ts`), the `git_token` self-heal (#3371, a platform inference that no installation covers the repo).
   * (A GitHub transfer A -> B is not one of these for A: `upsertRepositoriesForAccount`
   * re-points the row, so A's key `(A, old full_name)` keeps no row, or only a same-named
   * twin whose own `enabled` then decides (the cancel spares it — the `not exists …
   * e.enabled` clause in `cancelPendingTasksOfDisabledReposTx`). With no twin, A's watch
   * settles `repo_unknown`. The row it lands on B can be `false` under B's tier; work B
   * already holds under that name belongs to a twin of B's, whose own row decides.)
   * An installation revoke or an unexplained refusal stays parked. The pull request is
   * never touched. For THIS babysit half, re-enabling the repo is the undo, and it works
   * because the PR's next webhook finds this row as a dead owner `driveForPr` re-anchors
   * (auto-merge repo) or escalates to a human — which is why the reason is deliberately
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`.
   *
   * SECOND PRODUCER, the queue half (plan 101 slice 01 §C): both disable queries — so all
   * three callers above, the self-heal included — and the install upsert on a transfer
   * (A's pending work under A's old key, `source: 'transferred_out'`) run `cancelPendingTasksOfDisabledReposTx`
   * (`packages/db/src/queries/tasks/repo-disabled.ts`) in the disable's own transaction,
   * cancelling the repo's `pending` tasks with this reason unless a same-named row of the
   * account is still enabled. Before it, a disabled repo's queue waited for a box forever
   * (five rows on prod since 2026-08-07, tripping `queue_stalled` hourly). `assigned` /
   * `in_progress` rows are box-held and not touched there. Re-enabling the repo does NOT
   * restore these cancelled pending tasks — no enable path re-pends them. Only an explicit
   * operator `redispatchTask` (`packages/db/src/queries/tasks/operator.ts`, which does not
   * exclude `cancelled`) can bring one back, row by row.
   */
  RepoDisabled: 'repo_disabled',
  /**
   * WE HOLD NO REPOSITORY ROW FOR A PARKED BABYSIT WATCH'S REPO. The babysit
   * reconciler's point gate (`resolveActingRepoByFullName`) found no `repositories` row
   * for the task's `repo_full_name` under its account's live installation, so there is
   * no repo id to drive it with and the watch can never progress. Settled by
   * `repoUnknownSettlePort` (`apps/worker/src/babysit/repo-unknown-settle.ts`) instead of
   * re-logging a `warn` every tick (deep sweep 02-13c: the gardener's
   * `developerz-ai/box-maintenance` sentinel, 288 lines a day from four tasks).
   *
   * NOT {@link RepoDisabled}: a disabled repo has a row its owner switched off; this one
   * has none. NOT in `OPERATOR_STOP_CANCEL_REASONS`: nobody acted, and if a row for the
   * PR's repo appears later (a rename, a re-onboard) its next webhook reaches the
   * dead-owner rescue exactly as for {@link RepoDisabled}.
   */
  RepoUnknown: 'repo_unknown',
  /**
   * TRIAGE CUT THIS TASK'S PULL REQUEST INTO A SPLIT. Babysit stopped the PR's fix rounds
   * (`ci_not_converging`) and filed a split ask; once triage schedules it, the follow-through
   * (`supersedeSplitSource`, `packages/db/src/queries/plan-groups/split-supersede.ts`) stamps
   * `superseded_by` = the split's tasks, regroups every open plan slot the task held onto them,
   * and cancels it with this reason. The worker then closes the PR with a comment naming them.
   *
   * IN `OPERATOR_STOP_CANCEL_REASONS`: the follow-through does the operator's `task cancel`
   * for them, and the retired PR is not an orphan a dead-owner rescue may re-anchor (a watch
   * on it could merge the very PR the split replaced).
   */
  SupersededBySplit: 'superseded_by_split',
  /**
   * THE REVIEW STALLED ON EVERY RUN IT WAS GIVEN. A box-held review with no progress for
   * 30 minutes, or a run older than 120, is re-queued on another box by
   * `supersedeStalledReviewTask` (`packages/db/src/queries/tasks/review-expiry.ts`), from the
   * check expiry or the stuck-task sweep's review pass; once `REVIEW_STALL_MAX_RUNS` runs
   * have stalled, the same statement ends it here, bumping the fence. Before the bound a
   * stalled review held its PR's merge gate on `review_in_flight` for hours
   * (tesote/tesote.ai#2732, 2026-09-28).
   *
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted, and the head is still open to
   * a re-review (a push, `review rerun`, or babysit's `review_absent` re-review).
   */
  ReviewCheckExpired: 'review_check_expired',
  /**
   * NO BOX COULD EVER RUN THIS REVIEW. A `pending` `pr_review` no box claimed, on an
   * account with no enrolled (non-revoked) box that could serve it, older than
   * {@link UNSERVED_REVIEW_EXPIRY_DAYS} — ended by `expireUnservedPendingReview`
   * (`packages/db/src/queries/tasks/unserved-review-expiry.ts`), bumping the fence. A
   * `pending` review holds its PR's merge gate (`review_in_flight`), so before this the
   * gate was held forever on a PR nothing would ever review. Ending it does NOT open the
   * gate: the merge gate refuses `review_unserved` until a box is enrolled and a fresh
   * review runs. The customer is told {@link REVIEW_UNSERVED_SENTENCE}.
   *
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted, and the head is open to a
   * re-review once a box is enrolled (a push mints a fresh review task).
   */
  ReviewUnserved: 'review_unserved',
  /**
   * A NEWER ONBOARDING PASS REPLACED THIS ONE (#6081). A `setup` pass that `completed`
   * with no pull request linked can never stamp `ai_first_at`, so `cron.onboarding_backfill`
   * mints its successor — and `supersedeAndMintOnboardingPass`
   * (`packages/db/src/queries/tasks/onboarding-owed.ts`) cancels the predecessor with this
   * reason IN THE SAME TRANSACTION as the mint, under a row lock and only while it is still
   * `completed` with a null `pull_request_number`. That is what makes a late
   * `linkTaskToPr` harmless: the row it lands on is already terminal, so it can never read
   * live again beside its successor.
   *
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted, and a pull request the old
   * pass opened is the same `dz/onboarding` PR the successor adopts.
   */
  OnboardingSuperseded: 'onboarding_superseded',
  /**
   * A RE-ARMED SPLIT REPLACED THIS ONE. The UPDATE is `cancelRearmedSplitTasks`
   * (`packages/db/src/queries/plan-groups/split-supersede.ts`), driven and audited by the
   * babysit split follow-through (`apps/worker/src/babysit/split-followthrough.ts`); the
   * semantics are documented at those writes.
   */
  SupersededByRearm: 'superseded_by_rearm',
  /**
   * A HELD BABYSIT FOLLOW-UP WAS REFILED AS A FRESH ROW (#6343). A `pending`
   * `pr_babysit` follow-up whose hold will not lift in any window the PR can wait
   * (the `followup_held` park, #6338) keeps the PR's one in-flight slot forever, so
   * `supersedeAndMintBabysitRound` (`packages/db/src/queries/tasks/babysit-supersede.ts`)
   * cancels it under a row lock and mints its successor IN THE SAME TRANSACTION, stamping
   * `superseded_by` with the fresh row's id and bumping the fence. The CAS on
   * `status = 'pending'` AND the caller-read `lease_epoch` is what keeps the cancel honest:
   * a row a box claimed between the caller's read and the lock answers `deduped` and is
   * left alone.
   *
   * Reader this release, writer next: the reason ships with its reader a full release
   * before #6344 wires the call (the stored closed-set rule,
   * `packages/db/docs/migration-rules.md`).
   *
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted, and the PR's watch lives on
   * the successor row — the parked row is not orphaned, it was refiled.
   */
  SupersededByRescue: 'superseded_by_rescue',
  /**
   * THE HEAD'S CI WENT RED, SO ITS REVIEW IS SKIPPED (#6718, owner ruling 2026-10-20: a
   * reviewer does not review a head its own CI already refused, like a human). Stamped by
   * `cancelReviewForCiRed` (`packages/db/src/queries/tasks/ci-pending.ts`) when the worker
   * grades a CI completion red on an unstarted review's head, and by `cancelIrrelevantReview`
   * when a box reads the head red at claim (`review_irrelevant`). Both bump the fence.
   *
   * REVIVABLE, and that is why it is its own member rather than {@link Superseded}: a flaky
   * check re-run turning the SAME head green must still get its review, so
   * `reviveCiRedReview` puts the row back to `pending` — refused when a newer review of the
   * PR exists. NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted.
   */
  CiRed: 'ci_red',
  /**
   * THE PULL REQUEST IS A DRAFT, SO ITS REVIEW IS SKIPPED (#6718). A review box that reads
   * the PR as a draft at claim ends the run `review_irrelevant`, landed here by
   * `cancelIrrelevantReview`. And the mothership's own door: a `converted_to_draft` delivery
   * ends the PR's reviews through `supersedeReviewTasks` with this reason (the running one by
   * request), so the check says "draft", never "a newer commit was pushed". `ready_for_review`
   * mints the head again, and `mintReviewTask` revives the cancelled row under its id. NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted.
   */
  PrDraft: 'pr_draft',
  /**
   * THE TASK'S LINKED ISSUE CLOSED (#7105). A fact, not a judgement: the queue controller
   * (`cron.queue_controller`, `apps/worker`) reads a `pending` task whose originating issue
   * is closed and cancels it through `packages/db/src/queries/tasks/queue-controller.ts`,
   * a CAS on `status = 'pending'` AND the read `lease_epoch` under `SKIP LOCKED`, never a
   * row with work in progress — the one rule is `queueWipSql`
   * (`packages/db/src/queries/tasks/queue-wip.ts`): a cancelled row's branch is deleted.
   * Never while another task of the same ask owns a PR or merged: that issue most likely
   * closed through our own PR, and the remaining slices are the queue judge's to drop.
   * LEVEL-TRIGGERED and REVIVABLE: the same controller re-pends the row when the
   * issue reopens, so a close is never a person's call to end the work.
   *
   * Reader this release, writer next: the reason ships with its reader a full release
   * before the cron calls the statement (the stored closed-set rule,
   * `docs/standards/stored-closed-sets.md`), the {@link SupersededByRescue} precedent.
   *
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted, and a reopen undoes it.
   */
  IssueClosed: 'issue_closed',
  /**
   * THE QUEUE JUDGE FOLDED THIS TASK INTO ANOTHER (#7105). The `queue.judge` consumer
   * (`apps/agents-manager`) answered `merge_into` for a pending discretionary or signal
   * task, `validateQueueJudgement` (`value-objects/queue-judgement.ts`) accepted it, and
   * `packages/db/src/queries/tasks/queue-judgement-apply.ts` cancels it with
   * `superseded_by` naming the target and the judge's amendment appended to the target,
   * in one transaction, CAS on pending + `lease_epoch`. Never a human-class task, never a
   * row with work in progress (`queueWipSql`, as {@link IssueClosed}).
   *
   * Reader this release, writer next — as {@link IssueClosed}. REVIVABLE: a judge
   * `revive` or an operator `redispatchTask` restores the row and strips the amendment.
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted.
   */
  QueueMerged: 'queue_merged',
  /**
   * THE QUEUE JUDGE DROPPED THIS TASK (#7105): `drop` with a kind (`premise_gone`,
   * `obsolete`, `low_value`, `duplicate`) and evidence that resolves to a merged PR or a
   * task on the board, landed by `packages/db/src/queries/tasks/queue-judgement-apply.ts`
   * under the same CAS as {@link QueueMerged}. Never a human-class task, and never
   * `low_value` on a signal-class one (`validateQueueJudgement`).
   *
   * Reader this release, writer next — as {@link IssueClosed}. REVIVABLE by a judge
   * `revive`, an operator `redispatchTask`, or a triage `schedule` of the same work.
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted.
   */
  QueueDropped: 'queue_dropped',
  /**
   * NO ELIGIBLE MACHINE CLAIMED THE MODEL-CALL OFFER WITHIN 60 S; THE CLUSTER RAN IT
   * (#6759, epic #6552). A `triage_judge` / `kb_fold` task is an offer to the account's
   * org machines; its caller takes the call back when nobody claims it, as a CAS on
   * `pending`, so a claim that won first keeps the row.
   *
   * Written by `cancelUnclaimedModelCallOffer` (`@developerz/db`, #6760), run by the
   * dead-caller sweep and #6762's shell. NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted.
   */
  MachineUnclaimed: 'machine_unclaimed',
  /**
   * THE MACHINE THAT CLAIMED THE OFFER DID NOT ANSWER BY ITS DEADLINE; THE CLUSTER RAN IT
   * (#6759). Landed on the box-held row once `model_call_jobs.deadline_at` has passed on
   * the DB clock, by `cancelModelCallOfferAtDeadline` (#6760), as {@link MachineUnclaimed}.
   * NOT in `OPERATOR_STOP_CANCEL_REASONS`: no operator acted.
   */
  MachineDeadline: 'machine_deadline',
  /**
   * THE MODEL-CALL OFFER THIS TASK CARRIES IS SPENT: its job was settled, or its ONE claim
   * was already stamped (`model_call_jobs.claimed_at`), so no machine can ever claim it
   * again (`markModelCallClaimed` refuses both). Landed instead of a re-pend, which would
   * leave a `pending` row every claim refuses forever (the 2026-10-30 `kb_fold` loop:
   * `model_call_claim_rolled_back kind=offer_unclaimable` on ~10 boxes per job).
   *
   * Two writers, both `@developerz/db`: `requestSpentModelCallOfferCancels` stamps it as a
   * REQUESTED cancel (ruling S1, `queries/tasks/cancel-request.ts`) on a box-held offer task
   * whose offer settled while the box still ran it, and on any box-held offer task an exit
   * would otherwise re-pend — the exit then lands it `cancelled` with this reason (the box
   * release, `releaseBoxClaimsTx`, writes it in its one statement via the same file's
   * `stampedReasonSql`); and
   * `cancelStrandedModelCallTask` ends an already-stranded `pending` row. NOT in
   * `OPERATOR_STOP_CANCEL_REASONS`: no operator acted.
   */
  ModelCallSpent: 'model_call_spent',
} as const;

/**
 * How long a `pending` review may wait on an account no box can serve before
 * {@link TaskCancelReason.ReviewUnserved} ends it. The failure it prevents: a review
 * gate held forever on a pull request no box will ever review — `pending` holds the
 * merge gate, and nothing else ends a review nobody claims. Seven days leaves a week to
 * enroll a box, and the customer sentence states the same number, from here.
 */
export const UNSERVED_REVIEW_EXPIRY_DAYS = 7;

/** What the customer is told when a review ends {@link TaskCancelReason.ReviewUnserved}. */
export const REVIEW_UNSERVED_SENTENCE = `No box enrolled for this account can run a review, so this review was ended after ${UNSERVED_REVIEW_EXPIRY_DAYS} days. We cannot merge this pull request until a box is enrolled and a review runs. Enroll a box, then push or request a re-review.`;
export const TaskCancelReasonSchema = z.enum(TaskCancelReason);
export type TaskCancelReason = z.infer<typeof TaskCancelReasonSchema>;

/**
 * WHO WRITES a {@link TaskCancelReason} — one declaration per member, the
 * `TASK_FAILURE_PRODUCER` treatment.
 *
 * `Record` over the closed enum: a member added without naming its writer
 * fails typecheck HERE, and `task-cancel-reason.test.ts` checks each named
 * anchor against its file rather than believing it. There is no `wire` arm and
 * never will be: no box writes this column — the runner's own cancel
 * vocabulary never leaves the process (see the enum's header). Every writer is
 * a platform statement, so the shape is the producer list and nothing else.
 *
 * The generic `transition` appears under {@link TaskCancelReason.Abandoned}
 * and {@link TaskCancelReason.DispatchSkipped} as the CALL SITE that supplies
 * the reason, not as `packages/db/src/queries/tasks/lifecycle.ts#transitionTask` itself: that statement
 * writes `input.cancelReason` for whichever caller routes a cancel through it,
 * so naming it would anchor on a file that mentions no member — the same hole
 * `worktree-store.ts`'s indirect stamp taught the failure table.
 */
export const TASK_CANCEL_REASON_PRODUCER: Record<
  TaskCancelReason,
  {
    readonly producedBy: readonly string[];
    /**
     * A reason shipped READER-FIRST, a release before any statement writes it: `producedBy`
     * is empty and this names the issue whose PR adds the writer (and moves the entry to
     * `producedBy`). `task-cancel-reason.test.ts` refuses an entry with both or neither, and
     * goes red the moment production source stamps the reason while this is set.
     */
    readonly readerFirst?: { readonly producer: string };
  }
> = {
  [TaskCancelReason.Superseded]: {
    producedBy: [
      'packages/db/src/queries/tasks/pr-lane.ts#supersedeReviewTasks',
      'packages/db/src/queries/tasks/ci-pending.ts#cancelIrrelevantReview',
    ],
  },
  [TaskCancelReason.PrSettled]: {
    producedBy: [
      'packages/db/src/queries/tasks/pr-lane.ts#retireTasksForClosedPr',
      'packages/db/src/queries/tasks/ci-pending.ts#cancelIrrelevantReview',
    ],
  },
  [TaskCancelReason.OperatorStop]: {
    producedBy: ['packages/db/src/queries/tasks/operator.ts#cancelTask'],
  },
  [TaskCancelReason.PlanStop]: {
    producedBy: ['packages/db/src/queries/tasks/cancel-plan.ts#cancelPlanTasks'],
  },
  [TaskCancelReason.PlanRevised]: {
    producedBy: ['packages/db/src/queries/plan-groups/revise.ts#revisePlanGroups'],
  },
  [TaskCancelReason.Abandoned]: {
    producedBy: ['apps/worker/src/babysit/driver.ts#driveTask'],
  },
  [TaskCancelReason.DispatchSkipped]: {
    producedBy: ['apps/worker/src/consumers/gh-dispatch.lane.ts#ghDispatchAdvance'],
  },
  [TaskCancelReason.SupersededByRejudge]: {
    producedBy: ['packages/db/src/queries/tasks/rejudge.ts#cancelSupersededTriageTasks'],
  },
  [TaskCancelReason.RepoDisabled]: {
    producedBy: [
      'apps/worker/src/babysit/repo-disabled-settle.ts#repoDisabledSettlePort',
      'packages/db/src/queries/tasks/repo-disabled.ts#cancelPendingTasksOfDisabledReposTx',
    ],
  },
  [TaskCancelReason.RepoUnknown]: {
    producedBy: ['apps/worker/src/babysit/repo-unknown-settle.ts#repoUnknownSettlePort'],
  },
  [TaskCancelReason.ReviewCheckExpired]: {
    producedBy: ['packages/db/src/queries/tasks/review-expiry.ts#supersedeStalledReviewTask'],
  },
  [TaskCancelReason.ReviewUnserved]: {
    producedBy: [
      'packages/db/src/queries/tasks/unserved-review-expiry.ts#expireUnservedPendingReview',
    ],
  },
  [TaskCancelReason.SupersededBySplit]: {
    producedBy: ['packages/db/src/queries/plan-groups/split-supersede.ts#supersedeSplitSource'],
  },
  [TaskCancelReason.OnboardingSuperseded]: {
    producedBy: ['packages/db/src/queries/tasks/onboarding-owed.ts#supersedeAndMintOnboardingPass'],
  },
  [TaskCancelReason.SupersededByRearm]: {
    producedBy: [
      'packages/db/src/queries/plan-groups/split-supersede.ts#cancelRearmedSplitTasks',
      'apps/worker/src/babysit/split-followthrough.ts',
    ],
  },
  [TaskCancelReason.SupersededByRescue]: {
    producedBy: ['packages/db/src/queries/tasks/babysit-supersede.ts#supersedeAndMintBabysitRound'],
  },
  [TaskCancelReason.CiRed]: {
    producedBy: [
      'packages/db/src/queries/tasks/ci-pending.ts#cancelReviewForCiRed',
      'packages/db/src/queries/tasks/ci-pending.ts#cancelIrrelevantReview',
    ],
  },
  [TaskCancelReason.PrDraft]: {
    producedBy: [
      'packages/db/src/queries/tasks/ci-pending.ts#cancelIrrelevantReview',
      'packages/db/src/queries/tasks/pr-lane.ts#supersedeReviewTasks',
    ],
  },
  // #7105, reader-first: the statements ship now, uncalled. Their callers arrive a release
  // later — `cron.queue_controller` (issue_closed) and the `queue.judge` consumer
  // (queue_merged, queue_dropped).
  [TaskCancelReason.IssueClosed]: {
    producedBy: ['packages/db/src/queries/tasks/queue-controller.ts'],
  },
  [TaskCancelReason.QueueMerged]: {
    producedBy: ['packages/db/src/queries/tasks/queue-judgement-apply.ts'],
  },
  [TaskCancelReason.QueueDropped]: {
    producedBy: ['packages/db/src/queries/tasks/queue-judgement-apply.ts'],
  },
  // #6760: the two model-call take-backs, run by `cron.model_call_sweep` (and #6762's shell).
  [TaskCancelReason.MachineUnclaimed]: {
    producedBy: ['packages/db/src/queries/model-call-jobs.ts'],
  },
  [TaskCancelReason.MachineDeadline]: {
    producedBy: ['packages/db/src/queries/model-call-jobs.ts'],
  },
  [TaskCancelReason.ModelCallSpent]: {
    producedBy: [
      'packages/db/src/queries/tasks/model-call-spent.ts',
      'packages/db/src/queries/model-call-stranded.ts',
    ],
  },
};
